import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import PhotoUpload from "@/components/common/PhotoUpload";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Truck, Wrench, Package, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PURPOSE_OPTIONS = [
  { v: "pickup", l: "Ambil Bahan Baku dari Supplier", icon: Package, color: "sky" },
  { v: "bengkel", l: "Ke Bengkel / Servis", icon: Wrench, color: "amber" },
];

const initial = {
  vehicle_no: "",
  driver_name: "",
  purpose_type: "pickup",
  destination: "",
  supplier_name: "",
  material_name: "Oli Bekas",
  estimated_qty: "",
  notes: "",
  tare_weight: "",
  photo_weigh_truck: "",
  photo_weigh_display: "",
  photo_gate_out: "",
};

const fmt = (n) => (n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("id-ID") : "—");

export function InternalDispatchInner() {
  const { user } = useAuth();
  const canInput = ["admin", "owner", "admin_kantor", "admin_pabrik", "security"].includes(user?.role);

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [returning, setReturning] = useState(null);   // dispatch object
  const [rForm, setRForm] = useState({
    gross_weight: "", photo_weigh_truck: "", photo_weigh_display: "",
    photo_gate_in: "", return_notes: "",
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setR = (k) => (v) => setRForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const load = () => api.get("/gate/dispatch").then((r) => setRows(r.data)).catch(() => {});
  useAutoRefresh(load);

  const submit = async (e) => {
    e.preventDefault();
    if (!canInput) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tare_weight: parseFloat(form.tare_weight) || 0,
        estimated_qty: form.estimated_qty ? parseFloat(form.estimated_qty) : 0,
      };
      await api.post("/gate/dispatch", payload);
      toast.success(`Truk internal ${form.vehicle_no} dispatched. Timbang kosong tercatat.`);
      setForm(initial);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const openReturn = (r) => {
    setReturning(r);
    setRForm({
      gross_weight: "", photo_weigh_truck: "", photo_weigh_display: "",
      photo_gate_in: "", return_notes: "",
    });
  };

  const submitReturn = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...rForm,
        gross_weight: rForm.gross_weight ? parseFloat(rForm.gross_weight) : 0,
      };
      const { data } = await api.post(`/gate/dispatch/${returning.id}/return`, payload);
      if (data.receiving_ref_id) {
        toast.success(`Truk kembali. Receiving otomatis dibuat (net ${fmt(data.net_weight)} kg). Silakan lanjut input Lab.`);
      } else {
        toast.success("Truk kembali dari bengkel — dicatat.");
      }
      setReturning(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const active = rows.filter((r) => r.status === "dispatched");
  const history = rows.filter((r) => r.status !== "dispatched");

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
      {/* LEFT — dispatch form */}
      {canInput && (
      <form onSubmit={submit} data-testid="dispatch-form" className="xl:col-span-3 rounded-xl bg-white border border-slate-200 p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
          <Truck size={22} weight="duotone" className="text-slate-800" />
          <div>
            <h3 className="font-heading font-bold text-slate-900">Dispatch Truk Internal (Keluar Kosong)</h3>
            <p className="text-[11px] text-slate-500">Truk PT Primergy keluar kosong untuk ambil bahan atau ke bengkel — wajib timbang kosong dulu.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Tujuan Keluar" required>
            <Select value={form.purpose_type} onValueChange={set("purpose_type")}>
              <SelectTrigger data-testid="dispatch-purpose" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PURPOSE_OPTIONS.map((p) => (
                  <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Nomor Truk PT" required>
            <Input data-testid="dispatch-vehicle" value={form.vehicle_no} onChange={set("vehicle_no")} placeholder="B 1234 XYZ" className="font-mono uppercase" required />
          </Field>
          <Field label="Nama Driver" required>
            <Input data-testid="dispatch-driver" value={form.driver_name} onChange={set("driver_name")} placeholder="Nama lengkap" required />
          </Field>
          <Field label={form.purpose_type === "pickup" ? "Lokasi Tujuan / Nama Supplier" : "Lokasi Bengkel"} required>
            <Input
              data-testid="dispatch-destination"
              value={form.destination}
              onChange={set("destination")}
              placeholder={form.purpose_type === "pickup" ? "CV Sinar Sejahtera" : "Bengkel Jaya Motor"}
              required
            />
          </Field>

          {form.purpose_type === "pickup" && (
            <>
              <Field label="Nama Supplier (resmi)">
                <Input data-testid="dispatch-supplier" value={form.supplier_name} onChange={set("supplier_name")} placeholder="untuk auto-Receiving" />
              </Field>
              <Field label="Jenis Bahan Baku">
                <Input data-testid="dispatch-material" value={form.material_name} onChange={set("material_name")} />
              </Field>
              <Field label="Estimasi Qty (Liter)">
                <Input data-testid="dispatch-qty" type="number" step="0.01" value={form.estimated_qty} onChange={set("estimated_qty")} placeholder="0" />
              </Field>
            </>
          )}

          <Field label="Tare Weight — Timbang Kosong (kg)" required>
            <Input
              data-testid="dispatch-tare"
              type="number"
              step="0.01"
              value={form.tare_weight}
              onChange={set("tare_weight")}
              className="font-mono text-lg h-11"
              required
            />
            <p className="text-[10.5px] text-slate-500 mt-1">Berat truk dalam keadaan kosong sebelum keluar.</p>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PhotoUpload label="Foto Truck di Jembatan Timbang" value={form.photo_weigh_truck} onChange={set("photo_weigh_truck")} testid="dispatch-photo-weigh-truck" required />
          <PhotoUpload label="Foto Display Timbang" value={form.photo_weigh_display} onChange={set("photo_weigh_display")} testid="dispatch-photo-weigh-display" required />
          <PhotoUpload label="Foto Kendaraan di Gerbang" value={form.photo_gate_out} onChange={set("photo_gate_out")} testid="dispatch-photo-gate-out" required />
        </div>

        <Field label="Catatan">
          <Textarea data-testid="dispatch-notes" rows={2} value={form.notes} onChange={set("notes")} placeholder="Nomor SPK, kontak driver, dll." />
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={() => setForm(initial)} data-testid="dispatch-reset">Reset</Button>
          <Button type="submit" disabled={submitting} data-testid="dispatch-submit" className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-5">
            {submitting ? "Menyimpan…" : "Timbang & Dispatch Keluar"}
          </Button>
        </div>
      </form>
      )}

      {/* RIGHT — active + history */}
      <div className={`${canInput ? "xl:col-span-2" : "xl:col-span-5"} space-y-4`}>
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center bg-amber-50">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800">Menunggu Kembali</div>
              <div className="text-[10.5px] text-amber-700 font-mono">{active.length} truk sedang di luar</div>
            </div>
            <button
              onClick={load}
              data-testid="dispatch-refresh"
              className="ml-auto p-1.5 rounded-md hover:bg-white text-amber-800"
              title="Refresh"
            >
              <ArrowClockwise size={15} weight="bold" />
            </button>
          </div>
          <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
            {active.length === 0 && (
              <div className="py-8 text-center text-[13px] text-slate-400">Tidak ada truk internal di luar.</div>
            )}
            {active.map((r) => {
              const opt = PURPOSE_OPTIONS.find((p) => p.v === r.purpose_type);
              const Icon = opt?.icon || Truck;
              return (
                <div key={r.id} className="p-3.5 flex gap-3 items-start">
                  <Icon size={20} weight="duotone" className={`text-${opt?.color || "slate"}-600 mt-0.5 shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-bold text-slate-900 text-[13.5px]">{r.vehicle_no}</div>
                    <div className="text-[11.5px] text-slate-700">{r.driver_name} · {r.destination}</div>
                    <div className="text-[10.5px] text-slate-500 mt-0.5">
                      Tare: <b className="font-mono">{fmt(r.tare_weight)} kg</b> · Keluar {fmtDate(r.dispatched_at)}
                    </div>
                    {r.purpose_type === "pickup" && r.material_name && (
                      <div className="text-[10.5px] text-slate-500">Akan ambil: {r.material_name}{r.estimated_qty ? ` (~${fmt(r.estimated_qty)} L)` : ""}</div>
                    )}
                    {canInput && (
                      <button
                        onClick={() => openReturn(r)}
                        data-testid={`dispatch-return-btn-${r.vehicle_no}`}
                        className="mt-2 px-3 py-1.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-[11.5px] font-semibold inline-flex items-center gap-1.5"
                      >
                        <CheckCircle size={13} weight="fill" /> Truk Kembali
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Riwayat Dispatch</div>
          </div>
          <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
            {history.length === 0 && (
              <div className="py-8 text-center text-[13px] text-slate-400">Belum ada riwayat.</div>
            )}
            {history.slice(0, 20).map((r) => (
              <div key={r.id} className="p-3 text-[11.5px]">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-slate-800">{r.vehicle_no}</span>
                  <span className="text-[10px] text-emerald-700 font-mono">Kembali {fmtDate(r.returned_at)}</span>
                </div>
                <div className="text-slate-600 mt-0.5">
                  {r.purpose_type === "pickup" ? "Pickup" : "Bengkel"} · {r.destination}
                  {r.net_weight > 0 && <> · Net <b className="font-mono">{fmt(r.net_weight)} kg</b></>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Return dialog */}
      <Dialog open={!!returning} onOpenChange={(o) => !o && setReturning(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Truk Kembali — {returning?.vehicle_no}</DialogTitle>
          </DialogHeader>
          {returning && (
            <form onSubmit={submitReturn} data-testid="dispatch-return-form" className="space-y-4 mt-2">
              <div className="rounded-md bg-slate-50 p-3 text-[11.5px]">
                <div><b>Tujuan:</b> {returning.purpose_type === "pickup" ? "Ambil Bahan" : "Bengkel"} · {returning.destination}</div>
                <div><b>Tare Weight:</b> <span className="font-mono">{fmt(returning.tare_weight)} kg</span></div>
                {returning.purpose_type === "pickup" && (
                  <div><b>Akan ambil:</b> {returning.material_name} {returning.estimated_qty ? `(est. ${fmt(returning.estimated_qty)} L)` : ""}</div>
                )}
              </div>

              {returning.purpose_type === "pickup" && (
                <>
                  <Field label="Gross Weight — Timbang Terisi (kg)" required>
                    <Input
                      data-testid="dispatch-return-gross"
                      type="number"
                      step="0.01"
                      value={rForm.gross_weight}
                      onChange={setR("gross_weight")}
                      className="font-mono text-lg h-11"
                      required
                    />
                    {rForm.gross_weight && (
                      <div className="text-[11px] font-mono text-emerald-700 mt-1">
                        Net weight ={fmt(parseFloat(rForm.gross_weight) - (returning.tare_weight || 0))} kg
                      </div>
                    )}
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <PhotoUpload label="Foto Truck di Timbang" value={rForm.photo_weigh_truck} onChange={setR("photo_weigh_truck")} testid="dispatch-return-photo-weigh-truck" required />
                    <PhotoUpload label="Foto Display Timbang" value={rForm.photo_weigh_display} onChange={setR("photo_weigh_display")} testid="dispatch-return-photo-weigh-display" required />
                  </div>
                </>
              )}

              <PhotoUpload label="Foto Kendaraan Kembali di Gerbang" value={rForm.photo_gate_in} onChange={setR("photo_gate_in")} testid="dispatch-return-photo-gate-in" required />

              <Field label="Catatan Kembali">
                <Textarea data-testid="dispatch-return-notes" rows={2} value={rForm.return_notes} onChange={setR("return_notes")} />
              </Field>

              {returning.purpose_type === "pickup" && (
                <div className="rounded-md bg-sky-50 border-l-4 border-sky-500 p-3 text-[12px] text-sky-900">
                  <b>Sinkron otomatis:</b> Setelah submit, sistem akan otomatis buat <b>Receiving Reference</b> baru dengan supplier, material & net weight di atas. Petugas Lab bisa langsung lanjut input hasil lab.
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setReturning(null)}>Batal</Button>
                <Button type="submit" data-testid="dispatch-return-submit" className="bg-slate-900 hover:bg-slate-800 text-white">
                  Konfirmasi Kembali
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
